import { XY } from "./xy";

export enum Direction {
	N = "N",
	NE = "NE",
	E = "E",
	SE = "SE",
	S = "S",
	SW = "SW",
	W = "W",
	NW = "NW",
}

// Lazy init to avoid circular dependency with XY (xy.ts imports direction.ts and vice versa)
let _deltas: Record<Direction, XY> | null = null;
function getDeltas(): Record<Direction, XY> {
	if (!_deltas) {
		_deltas = {
			[Direction.N]: new XY(0, -1),
			[Direction.NE]: new XY(1, -1),
			[Direction.E]: new XY(1, 0),
			[Direction.SE]: new XY(1, 1),
			[Direction.S]: new XY(0, 1),
			[Direction.SW]: new XY(-1, 1),
			[Direction.W]: new XY(-1, 0),
			[Direction.NW]: new XY(-1, -1),
		};
	}
	return _deltas;
}

export const ALL_DIRECTIONS: Direction[] = [
	Direction.N,
	Direction.NE,
	Direction.E,
	Direction.SE,
	Direction.S,
	Direction.SW,
	Direction.W,
	Direction.NW,
];

export function directionDelta(dir: Direction): XY {
	return getDeltas()[dir];
}

export function directionFromVector(vector: XY): Direction | null {
	const deltas = getDeltas();
	for (const dir of ALL_DIRECTIONS) {
		if (deltas[dir].equals(vector)) return dir;
	}
	return null;
}

/** Nearest of the 8 directions by angle, for arbitrary (non-unit) vectors. */
export function nearestDirectionFromVector(vector: XY): Direction | null {
	if (vector.x === 0 && vector.y === 0) return null;
	const octantsFromEast = Math.round(Math.atan2(vector.y, vector.x) / (Math.PI / 4));
	return ALL_DIRECTIONS[(((octantsFromEast + 2) % 8) + 8) % 8];
}

export function cycleDirection(dir: Direction, steps: number): Direction {
	const index = ALL_DIRECTIONS.indexOf(dir);
	const len = ALL_DIRECTIONS.length;
	const resultIndex = (((index + (steps % len)) % len) + len) % len;
	return ALL_DIRECTIONS[resultIndex];
}

export function isCardinal(dir: Direction): boolean {
	return dir === Direction.N || dir === Direction.S || dir === Direction.E || dir === Direction.W;
}

export function isIntermediate(dir: Direction): boolean {
	return !isCardinal(dir);
}

export function isDiagonal(dir: Direction): boolean {
	return !isCardinal(dir);
}

export function reverseDirection(dir: Direction): Direction {
	return cycleDirection(dir, 4);
}

// 90° counter-clockwise per turn — matches SimpleBoard.rotate().
export function rotateDirection(dir: Direction, turns90: number): Direction {
	return cycleDirection(dir, -2 * turns90);
}
